import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { UserPreferencesService } from './user-preferences.service';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

@Injectable({
  providedIn: 'root',
})
export class TimeZoneService implements OnDestroy {
  private readonly defaultTimeZone: string =
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  private userTimeZoneSubject = new BehaviorSubject<string>(
    this.defaultTimeZone
  );
  public readonly userTimeZone$: Observable<string> =
    this.userTimeZoneSubject.asObservable();
  private preferenceSubscription: Subscription;

  constructor(private userPreferences: UserPreferencesService) {
    this.preferenceSubscription = this.userPreferences.userTimezone$.subscribe(
      timezone => {
        if (timezone) this.setUserTimeZone(timezone);
      }
    );
  }

  public setUserTimeZone(timeZone: string): void {
    if (!timeZone) timeZone = this.defaultTimeZone;
    this.userTimeZoneSubject.next(timeZone);
  }

  public getUserTimeZone(): string {
    return this.userTimeZoneSubject.getValue();
  }

  /**
   * Converts a UTC date (from API) to user's timezone for display
   * @param utcDate The UTC date from API (string or Date object)
   * @param specificTimeZone Optional specific timezone to display in
   * @returns Date object in the target timezone
   */
  public convertToUserTimeZone(
    utcDate: Date | string | null,
    specificTimeZone?: string
  ): Date | null {
    if (!utcDate) return null;

    try {
      const userTimeZone =
        specificTimeZone || this.userTimeZoneSubject.getValue() || 'UTC';

      const parsedUtcDate = dayjs.utc(utcDate);
      const tzDate = parsedUtcDate.tz(userTimeZone);

      const year = tzDate.year();
      const month = tzDate.month();
      const day = tzDate.date();
      const hour = tzDate.hour();
      const minute = tzDate.minute();
      const second = tzDate.second();
      const millisecond = tzDate.millisecond();

      const localDate = new Date();
      localDate.setFullYear(year, month, day);
      localDate.setHours(hour, minute, second, millisecond);

      return localDate;
    } catch (error) {
      console.error('Error converting to user timezone:', error);

      try {
        return typeof utcDate === 'string'
          ? new Date(utcDate)
          : new Date(utcDate);
      } catch (fallbackError) {
        console.error('Error in fallback conversion:', fallbackError);
        return null;
      }
    }
  }

  /**
   * Converts a date from user's timezone to UTC
   * @param zonedDate The date in user's timezone (string or Date object)
   * @param specificTimeZone Optional specific source timezone (if not user's default)
   * @returns Date object in UTC
   */
  public convertToUTC(
    zonedDate: Date | string | null,
    specificTimeZone?: string
  ): Date | null {
    if (!zonedDate) return null;

    try {
      const sourceTimeZone =
        specificTimeZone || this.userTimeZoneSubject.getValue();
      const utcDate = dayjs.tz(zonedDate, sourceTimeZone).utc().toDate();
      return utcDate;
    } catch (error) {
      console.error('Error converting to UTC:', error);
      return null;
    }
  }

  /**
   * Converts a date from a specific timezone to UTC
   * @param date The date to convert (string or Date object)
   * @param sourceTimezone The source timezone of the date (optional, defaults to user timezone)
   * @param format Optional format string if the date is a string that needs specific parsing
   * @returns Date object in UTC
   */
  public convertSpecificTimezoneToUTC(
    date: Date | string,
    sourceTimezone?: string,
    format?: string
  ): Date | null {
    if (!date) {
      return null;
    }

    const timezone = sourceTimezone || this.userTimeZoneSubject.getValue();

    try {
      let dayjsDate;
      if (typeof date === 'string') {
        if (format) {
          dayjsDate = dayjs.tz(date, format, timezone);
        } else {
          dayjsDate = dayjs.tz(date, timezone);
        }
      } else {
        const d = date as Date;
        const dateString =
          `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}` +
          `T${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}`;
        dayjsDate = dayjs.tz(dateString, timezone);
      }

      const result = dayjsDate.utc().toDate();

      return result;
    } catch (error) {
      console.error('Error converting timezone to UTC:', error);
      console.error('Parameters:', { date, timezone, format });
      return null;
    }
  }

  /**
   * Format a date according to the given format and timezone
   *
   * @param date The date to format
   * @param format The format to use
   * @param timezone The timezone to use (optional)
   * @returns The formatted date string
   */
  formatDate(date: Date | string, format: string, timezone?: string): string {
    if (!date) {
      return '';
    }

    try {
      const tz = timezone || this.userTimeZoneSubject.getValue();

      let dayjsDate;
      if (typeof date === 'string') {
        dayjsDate = dayjs.utc(date).tz(tz);
      } else {
        dayjsDate = dayjs(date).tz(tz);
      }

      if (!dayjsDate.isValid()) {
        console.warn('Invalid date:', date);
        return '';
      }
      return dayjsDate.format(this.convertFormatToDayjs(format));
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  }

  /**
   * Converts Angular date format to dayjs format
   * @param format Angular date format
   * @returns dayjs compatible format
   */
  private convertFormatToDayjs(format: string): string {
    const dayjsFormat = format
      .replace(/yyyy/g, 'YYYY')
      .replace(/MM/g, 'MM')
      .replace(/dd/g, 'DD')
      .replace(/HH/g, 'HH')
      .replace(/mm/g, 'mm')
      .replace(/ss/g, 'ss');

    return dayjsFormat;
  }

  ngOnDestroy() {
    if (this.preferenceSubscription) {
      this.preferenceSubscription.unsubscribe();
    }
  }
}
